import React, { useState, useEffect, useRef } from 'react'
import st from './MergeDealsForm.module.scss'
import Button from 'components/CRM/UI/components/Button/Button'
import Loader from 'components/UI/Loader/Loader'
import swal from 'sweetalert'
import { superAxiosRequest } from 'axiosApi'

// Актуальные поля для объединения сделок на основе AddNewDeal
const STANDARD_FIELDS = [
  'client',
  'bonus_from',
  'reject_reason',
  'manager',
  'budget',
  'city',
  'year_of_study',
  'institution',
  'planned_admission_year',
  'branch',
  'program',
  'level_result',
  'planned_specialty',
  'country_of_admission',
  'institution_name',
  'meeting_format',
  'consultation_date',
  'meeting_summary',
  'birth_date',
  'is_documents_received',
  'visa_appointment',
  'program_cost',
  'client_annual_budget',
  'posttraining_perspectives',
  'partner_name',
  'reminder_date',
  'source',
]

const MergeDealsForm = ({
  dealsForMerge,
  onClose,
  onMerge,
  translate,
  funnelId,
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [checkedState, setCheckedState] = useState({})
  const [selectedData, setSelectedData] = useState({})
  const [loading, setLoading] = useState(true)

  // Состояния для статусов
  const [statuses, setStatuses] = useState([])
  const [selectedStatus, setSelectedStatus] = useState(null)
  const [selectedStatusName, setSelectedStatusName] = useState('')
  const [statusesLoading, setStatusesLoading] = useState(false)
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false)

  // Ref для отслеживания кликов вне селекта
  const statusDropdownRef = useRef(null)

  // Состояния для кастомных полей
  const [customFields, setCustomFields] = useState([])
  const [dealsCustomFieldsValues, setDealsCustomFieldsValues] = useState({})
  const [selectedCustomFields, setSelectedCustomFields] = useState({})

  // Закрытие дропдауна при клике вне его
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        statusDropdownRef.current &&
        !statusDropdownRef.current.contains(event.target)
      ) {
        setIsStatusDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // Загрузка статусов для воронки
  useEffect(() => {
    if (!funnelId) return

    const fetchStatuses = async () => {
      setStatusesLoading(true)
      try {
        const response = await superAxiosRequest({
          link: `integration/deal_statuses`,
          method: 'get',
          crm: true,
          params: { funnel_id: funnelId },
        })
        const rawData = response.data
        const statusesArray = Object.keys(rawData).map((key) => ({
          ...rawData[key],
          id: key,
          position: parseInt(rawData[key].position),
        }))
        statusesArray.sort((a, b) => a.position - b.position)
        setStatuses(statusesArray)
      } catch (error) {
        console.error('Ошибка при загрузке статусов:', error)
      } finally {
        setStatusesLoading(false)
      }
    }

    fetchStatuses()
  }, [funnelId])

  // Загрузка кастомных полей для воронки
  useEffect(() => {
    if (!funnelId) return

    const fetchCustomFields = async () => {
      try {
        const fieldsResponse = await superAxiosRequest({
          link: `integration/additional_funnel_deal_fields`,
          method: 'get',
          crm: true,
          params: { funnel_id: funnelId },
        })
        setCustomFields(fieldsResponse.data || [])

        const dealsValues = {}
        for (const deal of dealsForMerge) {
          const valuesResponse = await superAxiosRequest({
            link: `integration/additional_funnel_deal_field_values`,
            method: 'get',
            crm: true,
            params: {
              funnel_id: funnelId,
              deal_id: deal.id,
            },
          })
          dealsValues[deal.id] = valuesResponse.data || []
        }
        setDealsCustomFieldsValues(dealsValues)
        setLoading(false)

        const initSelectedCustomFields = {}
        fieldsResponse.data?.forEach((field) => {
          initSelectedCustomFields[field.id] = {
            valueId: null,
            value: null,
          }
        })
        setSelectedCustomFields(initSelectedCustomFields)
      } catch (error) {
        setLoading(false)
        console.error('Ошибка при загрузке кастомных полей:', error)
      }
    }

    fetchCustomFields()
  }, [funnelId, dealsForMerge])

  // Инициализация состояний для стандартных полей
  useEffect(() => {
    const initCheckedState = STANDARD_FIELDS.reduce((acc, field) => {
      acc[field] = null
      return acc
    }, {})

    const initSelectedData = {
      client: { id: null, name: '' },
      bonus_from: '',
      reject_reason: '',
      manager: { id: null, full_name: '' },
      budget: '',
      city: { id: null, name: '' },
      year_of_study: '',
      institution: '',
      planned_admission_year: '',
      branch: { id: null, name: '' },
      program: { id: null, name: '' },
      level_result: '',
      planned_specialty: '',
      country_of_admission: '',
      institution_name: '',
      meeting_format: '',
      consultation_date: '',
      meeting_summary: '',
      birth_date: '',
      is_documents_received: false,
      visa_appointment: '',
      program_cost: '',
      client_annual_budget: '',
      posttraining_perspectives: '',
      partner_name: '',
      reminder_date: '',
      source: '',
      funnels: [{ id: funnelId }],
    }

    setCheckedState(initCheckedState)
    setSelectedData(initSelectedData)
  }, [dealsForMerge, funnelId])

  // Функция для форматирования даты
  const formatClientDate = (dateString) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return date.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  // Получение значения стандартного поля из сделки
  const getDealFieldValue = (deal, field) => {
    switch (field) {
      case 'client':
        return {
          id: deal?.client?.id || deal?.lead?.client?.id,
          name: deal?.client?.name || deal?.lead?.client?.name || '',
        }
      case 'manager':
        return {
          id: deal?.manager?.id,
          full_name: deal?.manager?.full_name || deal?.manager?.name || '',
        }
      case 'city':
      case 'branch':
      case 'program':
        return deal[field] && typeof deal[field] === 'object'
          ? { id: deal[field].id, name: deal[field].name }
          : { id: null, name: '' }
      case 'is_documents_received':
        return (
          deal[field] === true || deal[field] === 'true' || deal[field] === 1
        )
      case 'source':
        return deal?.source?.name || deal?.source || ''
      default:
        if (deal[field] !== undefined && deal[field] !== null) {
          if (typeof deal[field] === 'object' && deal[field] !== null) {
            return deal[field].name || deal[field].value || deal[field]
          }
          return deal[field]
        }
        return field === 'budget'
          ? 0
          : field === 'is_documents_received'
            ? false
            : ''
    }
  }

  // Получение значения кастомного поля из сделки
  const getCustomFieldValue = (dealId, fieldId) => {
    const dealValues = dealsCustomFieldsValues[dealId] || []
    return dealValues.find((value) => value.additional_funnel_field === fieldId)
  }

  // Форматирование значения кастомного поля для отображения
  const formatCustomFieldValue = (field, value) => {
    if (!value) return 'Не указано'

    switch (field?.type) {
      case 'boolean':
        return value.value === true
          ? 'Да'
          : value.value === false
            ? 'Нет'
            : 'Не указано'
      case 'date':
        return value.value
          ? new Date(value.value).toLocaleDateString('ru-RU')
          : 'Не указано'
      case 'select':
        return value.value?.name || value.value || 'Не указано'
      default:
        return value.value || 'Не указано'
    }
  }

  // Обработчик выбора стандартного поля
  const handleFieldSelect = (field, dealId, value) => {
    setCheckedState((prev) => ({
      ...prev,
      [field]: dealId,
    }))

    setSelectedData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  // Обработчик выбора кастомного поля
  const handleCustomFieldSelect = (fieldId, dealId, value) => {
    setSelectedCustomFields((prev) => ({
      ...prev,
      [fieldId]: {
        valueId: value?.id || null,
        value: value?.value || null,
        fieldId: fieldId,
        dealId: dealId,
      },
    }))
  }

  // Обработчик выбора статуса
  const handleStatusSelect = (status) => {
    setSelectedStatus(status.id)
    setSelectedStatusName(status.name)
    setIsStatusDropdownOpen(false)
  }

  const handleClearStatus = () => {
    setSelectedStatus(null)
    setSelectedStatusName('')
    setIsStatusDropdownOpen(false)
  }

  const toggleStatusDropdown = () => {
    setIsStatusDropdownOpen(!isStatusDropdownOpen)
  }

  // Обработчик "Выбрать всё" для конкретной сделки
  const handleSelectAllFromDeal = (dealId, deal) => {
    const newCheckedState = { ...checkedState }
    const newSelectedData = { ...selectedData }

    STANDARD_FIELDS.forEach((field) => {
      const value = getDealFieldValue(deal, field)
      newCheckedState[field] = dealId
      newSelectedData[field] = value
    })

    setCheckedState(newCheckedState)
    setSelectedData(newSelectedData)

    const newSelectedCustomFields = { ...selectedCustomFields }
    customFields.forEach((field) => {
      const fieldValue = getCustomFieldValue(dealId, field.id)
      newSelectedCustomFields[field.id] = {
        valueId: fieldValue?.id || null,
        value: fieldValue?.value || null,
        fieldId: field.id,
        dealId: dealId,
      }
    })

    setSelectedCustomFields(newSelectedCustomFields)
  }

  // Обработчик объединения
  const handleMerge = async () => {
    if (isLoading) return

    if (!selectedData.client?.name) {
      return swal(
        'Ошибка!',
        'Нужно заполнить обязательное поле - Имя клиента!',
        'warning',
      )
    }

    setIsLoading(true)

    try {
      const additionalFieldValuesIds = Object.values(selectedCustomFields)
        .filter((item) => item?.valueId)
        .map((item) => item.valueId)

      const payload = {
        combined_deals_ids: dealsForMerge.map((deal) => deal.id),
        deal: {
          client: selectedData.client?.id || null,
          bonus_from: selectedData.bonus_from || null,
          reject_reason: selectedData.reject_reason || null,
          manager: selectedData.manager?.id || null,
          budget: selectedData.budget || 0,
          city: selectedData.city?.id || null,
          year_of_study: selectedData.year_of_study || null,
          institution: selectedData.institution || null,
          planned_admission_year: selectedData.planned_admission_year || null,
          branch: selectedData.branch?.id || null,
          program: selectedData.program?.id || null,
          level_result: selectedData.level_result || null,
          planned_specialty: selectedData.planned_specialty || null,
          country_of_admission: selectedData.country_of_admission || null,
          institution_name: selectedData.institution_name || null,
          meeting_format: selectedData.meeting_format || null,
          consultation_date: selectedData.consultation_date || null,
          meeting_summary: selectedData.meeting_summary || null,
          birth_date: selectedData.birth_date || null,
          is_documents_received: selectedData.is_documents_received || false,
          visa_appointment: selectedData.visa_appointment || null,
          program_cost: selectedData.program_cost || null,
          client_annual_budget: selectedData.client_annual_budget || null,
          posttraining_perspectives:
            selectedData.posttraining_perspectives || null,
          partner_name: selectedData.partner_name || null,
          reminder_date: selectedData.reminder_date || null,
          source: selectedData.source || null,
          funnels: [funnelId],
          status_deal_id: selectedStatus || null,
        },
        additional_funnel_deal_field_values_ids: additionalFieldValuesIds,
      }

      await onMerge(payload)
    } catch (error) {
      console.error('Ошибка при объединении сделок:', error)
    } finally {
      setIsLoading(false)
    }
  }

  // Рендер стандартного поля сделки
  const renderDealField = (
    deal,
    field,
    label,
    isObject = false,
    isBoolean = false,
    isColumn = false, // <-- Новый параметр
  ) => {
    const value = getDealFieldValue(deal, field)
    let displayValue = value

    if (isBoolean) {
      displayValue = value ? 'Да' : 'Нет'
    } else if (isObject && typeof value === 'object') {
      displayValue = value.name || value.full_name || ''
    } else if (field === 'budget' && value) {
      displayValue = `${value} ₸`
    } else if (
      (field === 'consultation_date' ||
        field === 'birth_date' ||
        field === 'reminder_date') &&
      value
    ) {
      displayValue = formatClientDate(value)
    }

    return (
      <div
        className={`${st.fieldRow} ${isColumn ? st.fieldColumn : ''}`}
        key={field}
      >
        <label className={st.fieldCheckboxLabel}>
          <input
            type='checkbox'
            checked={checkedState[field] === deal.id}
            onChange={() => handleFieldSelect(field, deal.id, value)}
            className={st.fieldCheckbox}
          />
          <span className={st.fieldName}>{label}</span>
        </label>
        <div className={st.fieldValue}>
          {displayValue !== undefined &&
          displayValue !== null &&
          displayValue !== ''
            ? displayValue
            : 'Не указано'}
        </div>
      </div>
    )
  }

  // Рендер кастомного поля сделки
  const renderCustomField = (deal, field) => {
    const fieldValue = getCustomFieldValue(deal.id, field.id)
    const isSelected = selectedCustomFields[field.id]?.dealId === deal.id

    return (
      <div className={st.fieldRow} key={`custom-${field.id}-${deal.id}`}>
        <label className={st.fieldCheckboxLabel}>
          <input
            type='checkbox'
            checked={isSelected}
            onChange={() =>
              handleCustomFieldSelect(field.id, deal.id, fieldValue)
            }
            className={st.fieldCheckbox}
          />
          <span className={st.fieldName}>{field.name}</span>
        </label>
        <div className={st.fieldValue}>
          {formatCustomFieldValue(field, fieldValue)}
        </div>
      </div>
    )
  }

  // Рендер стандартного поля финальной сделки
  const renderFinalField = (
    field,
    label,
    isObject = false,
    isBoolean = false,
    isColumn = false, // <-- Новый параметр
  ) => {
    const value = selectedData[field]
    let displayValue = value
    const isEmpty = isBoolean
      ? value === undefined || value === null
      : !value || (isObject && typeof value === 'object' && !value.id)

    if (isBoolean) {
      displayValue = value ? 'Да' : 'Нет'
    } else if (isObject && typeof value === 'object') {
      displayValue = value.name || value.full_name || ''
    } else if (field === 'budget' && value) {
      displayValue = `${value} ₸`
    } else if (
      (field === 'consultation_date' ||
        field === 'birth_date' ||
        field === 'reminder_date') &&
      value
    ) {
      displayValue = formatClientDate(value)
    }

    return (
      <div
        className={`${st.finalFieldRow} ${isColumn ? st.finalFieldColumn : ''}`}
        key={field}
      >
        <div className={st.finalFieldName}>{label}</div>
        <div
          className={`${st.finalFieldValue} ${isEmpty ? st.emptyField : ''}`}
        >
          {displayValue !== undefined &&
          displayValue !== null &&
          displayValue !== ''
            ? displayValue
            : 'Не выбрано'}
        </div>
      </div>
    )
  }

  // Рендер кастомного поля финальной сделки
  const renderFinalCustomField = (field) => {
    const selectedValue = selectedCustomFields[field.id]
    const isEmpty = !selectedValue?.value

    return (
      <div className={st.finalFieldRow} key={`final-custom-${field.id}`}>
        <div className={st.finalFieldName}>{field.name}</div>
        <div
          className={`${st.finalFieldValue} ${isEmpty ? st.emptyField : ''}`}
        >
          {formatCustomFieldValue(field, { value: selectedValue?.value })}
        </div>
      </div>
    )
  }

  // Рендер кастомного селекта для выбора статуса
  const renderStatusSelect = () => (
    <div className={st.customSelectContainer} ref={statusDropdownRef}>
      <div
        className={`${st.customSelectTrigger} ${!selectedStatus ? st.empty : ''}`}
        onClick={toggleStatusDropdown}
      >
        <span className={st.selectedStatusText}>
          {selectedStatusName || 'Выбрать статус'}
        </span>
        <span className={`${st.arrow} ${isStatusDropdownOpen ? st.open : ''}`}>
          ▼
        </span>
      </div>

      {isStatusDropdownOpen && (
        <div className={st.customSelectDropdown}>
          {statusesLoading ? (
            <div className={st.dropdownLoading}>Загрузка...</div>
          ) : (
            <>
              <div className={st.dropdownOption} onClick={handleClearStatus}>
                <span className={st.optionName}>-- Не выбран --</span>
              </div>
              {statuses.map((status) => (
                <div
                  key={status.id}
                  className={`${st.dropdownOption} ${selectedStatus === status.id ? st.selected : ''}`}
                  onClick={() => handleStatusSelect(status)}
                >
                  <span
                    className={st.statusColor}
                    style={{ backgroundColor: status.color || '#ccc' }}
                  />
                  <span className={st.optionName}>{status.name}</span>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  )

  // Рендер карточки сделки
  const renderDealCard = (deal, index) => (
    <div key={deal.id} className={st.horizontalDealCard}>
      <div className={st.dealHeader}>
        <div className={st.dealTitle}>
          <div
            className={st.selectAllContainer}
            onClick={() => handleSelectAllFromDeal(deal.id, deal)}
          >
            Выбрать всё
          </div>
          <h3>Профиль клиента</h3>
        </div>
      </div>

      <div className={st.dealFieldsGrid}>
        {renderDealField(deal, 'client', 'Клиент', true)}
        {renderDealField(deal, 'bonus_from', 'Бонус (указать имя от кого)')}
        {renderDealField(deal, 'manager', 'Ответственный', true)}
        {renderDealField(deal, 'budget', 'Бюджет, ₸')}
        {renderDealField(deal, 'city', 'Город', true)}
        {renderDealField(deal, 'year_of_study', 'Класс/Курс')}
        {renderDealField(deal, 'institution', 'Школа/универ')}
        {renderDealField(
          deal,
          'planned_admission_year',
          'Планируемый год поступления',
        )}
        {renderDealField(deal, 'branch', 'Филиал', true)}
        {renderDealField(deal, 'program', 'Программа', true)}
        {renderDealField(deal, 'level_result', 'Уровень английского')}
        {renderDealField(deal, 'country_of_admission', 'Страна поступления')}
        {renderDealField(
          deal,
          'institution_name',
          'Название учебного заведения',
        )}
        {renderDealField(deal, 'meeting_format', 'Формат встречи')}
        {renderDealField(
          deal,
          'consultation_date',
          'Время и дата консультации',
        )}
        {renderDealField(deal, 'birth_date', 'День рождения')}
        {renderDealField(
          deal,
          'is_documents_received',
          'Документы получены',
          false,
          true,
        )}
        {renderDealField(deal, 'visa_appointment', 'Запись на визу')}
        {renderDealField(deal, 'program_cost', 'Стоимость программы')}
        {renderDealField(deal, 'client_annual_budget', 'Бюджет клиента на год')}
        {renderDealField(
          deal,
          'posttraining_perspectives',
          'Перспективы после обучения',
        )}
        {renderDealField(deal, 'partner_name', 'Название партнера')}
        {renderDealField(deal, 'reminder_date', 'Напомнить о нас')}
        {renderDealField(deal, 'source', 'Откуда пришли')}
        {renderDealField(deal, 'reject_reason', 'Причина отказа')}

        {renderDealField(
          deal,
          'planned_specialty',
          'Какую специальность вы планируете выбрать',
          false,
          false,
          true,
        )}
        {renderDealField(
          deal,
          'meeting_summary',
          'Краткое описание встречи',
          false,
          false,
          true,
        )}

        {customFields.length > 0 && (
          <>
            <div className={st.fieldSeparator}></div>
            {customFields.map((field) => renderCustomField(deal, field))}
          </>
        )}
      </div>
    </div>
  )

  // Рендер блока с окончательной сделкой
  const renderFinalDeal = () => (
    <div className={st.finalDealContainer}>
      <div className={st.wrapper}>
        <div className={st.finalDealHeader}>
          <h3>Объединенная сделка</h3>
          <div className={st.finalDealSubtitle}>
            Будет создана с выбранными полями
          </div>
        </div>

        <div className={st.finalDealCard}>
          <div className={st.finalFieldsGrid}>
            {renderFinalField('client', 'Клиент', true)}
            {renderFinalField('bonus_from', 'Бонус (указать имя от кого)')}
            {renderFinalField('manager', 'Ответственный', true)}
            {renderFinalField('budget', 'Бюджет, ₸')}
            {renderFinalField('city', 'Город', true)}
            {renderFinalField('year_of_study', 'Класс/Курс')}
            {renderFinalField('institution', 'Школа/универ')}
            {renderFinalField(
              'planned_admission_year',
              'Планируемый год поступления',
            )}
            {renderFinalField('branch', 'Филиал', true)}
            {renderFinalField('program', 'Программа', true)}
            {renderFinalField('level_result', 'Уровень английского')}
            {renderFinalField('country_of_admission', 'Страна поступления')}
            {renderFinalField(
              'institution_name',
              'Название учебного заведения',
            )}
            {renderFinalField('meeting_format', 'Формат встречи')}
            {renderFinalField('consultation_date', 'Время и дата консультации')}
            {renderFinalField('birth_date', 'День рождения')}
            {renderFinalField(
              'is_documents_received',
              'Документы получены',
              false,
              true,
            )}
            {renderFinalField('visa_appointment', 'Запись на визу')}
            {renderFinalField('program_cost', 'Стоимость программы')}
            {renderFinalField('client_annual_budget', 'Бюджет клиента на год')}
            {renderFinalField(
              'posttraining_perspectives',
              'Перспективы после обучения',
            )}
            {renderFinalField('partner_name', 'Название партнера')}
            {renderFinalField('reminder_date', 'Напомнить о нас')}
            {renderFinalField('source', 'Откуда пришли')}
            {renderFinalField('reject_reason', 'Причина отказа')}

            {renderFinalField(
              'planned_specialty',
              'Какую специальность вы планируете выбрать',
              false,
              false,
              true,
            )}
            {renderFinalField(
              'meeting_summary',
              'Краткое описание встречи',
              false,
              false,
              true,
            )}

            {customFields.length > 0 && (
              <>
                <div className={st.finalFieldSeparator}></div>
                {customFields.map((field) => renderFinalCustomField(field))}
              </>
            )}

            <div className={st.finalFieldSeparator}></div>

            <div className={st.finalFieldRow}>
              <div className={st.finalFieldName}>Статус сделки:</div>
              <div className={st.finalFieldValue}>{renderStatusSelect()}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )

  return (
    <div className={st.mergeModalOverlay}>
      <div className={st.mergeModal}>
        <div className={st.mergeModalHeader}>
          <h2>Объединение сделок ({dealsForMerge.length})</h2>
          <button className={st.closeButton} onClick={onClose}>
            ×
          </button>
        </div>

        <div className={st.mergeModalContent}>
          {isLoading || loading || statusesLoading ? (
            <div className={st.loadingContainer}>
              <Loader />
              {isLoading && <p>Объединение сделок...</p>}
            </div>
          ) : (
            <>
              <div className={st.mergeInstructions}>
                <p>
                  Выберите поля для объединенной сделки. Отметьте чекбоксы рядом
                  с нужными значениями. Можно выбрать "Выбрать всё" для
                  копирования всех полей из одной сделки.
                </p>
              </div>

              <div className={st.horizontalMergeContainer}>
                <div className={st.dealsRowContainer}>
                  <div className={st.dealsRow}>
                    {dealsForMerge.map((deal, index) =>
                      renderDealCard(deal, index),
                    )}
                  </div>
                </div>

                {renderFinalDeal()}
              </div>

              <div className={st.mergeActions}>
                <Button
                  text='Отмена'
                  onClick={onClose}
                  className={st.cancelButton}
                  isDisabled={isLoading}
                />
                <Button
                  text={isLoading ? 'Объединение...' : 'Объединить сделки'}
                  onClick={handleMerge}
                  className={st.mergeButton}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default MergeDealsForm
